/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { replace, times, isEmpty } from 'lodash';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { ToggleSwitch } from '@broadleaf/admin-components/dist/form';
import { hooks, unstable } from '@broadleaf/admin-components';
const { useEventCallback, useDebouncedCallback } = hooks;
const { useFormatNumber } = unstable;

export const AuroraHTMLEditor: React.FC<HTMLEditorProps> = ({
  className,
  editorClassName,
  htmlSourceClassName,
  name,
  onBlur,
  onChange,
  toolbarClassName,
  wrapperClassName,
  value,
  disabled = false,
  editorLabel = 'Editor',
  htmlLabel = 'HTML',
  wrapperId = 1
}) => {
  const [editorState, setEditorState] = useState(getEditorStateForHTML(value));
  const [showHtml, setShowHtml] = useState(false);
  const debouncedOnChange = useDebouncedCallback(onChange, 300);

  useEffect(
    () => {
      const editorValue = getHTMLForEditorState(editorState);
      if (value !== editorValue) {
        const nextEditorState = getEditorStateForHTML(value);
        setEditorState(nextEditorState);
      }
    },
    // eslint-disable-next-line
    [value]
  );

  const handleBlur = useEventCallback(
    e => {
      onBlur &&
        onBlur({
          ...e,
          target: {
            ...e.target,
            type: 'textarea',
            name,
            value
          }
        });
    },
    [onBlur, name, value]
  );

  return (
    <div
      className={classNames(
        'HTMLEditor',
        'tw-flex tw-w-full tw-flex-col tw-items-start disabled:tw-pointer-events-none disabled:tw-select-none',
        className,
        {
          disabled: !!disabled
        }
      )}
    >
      <ToggleSwitch
        className="tw-my-1"
        disabled={disabled}
        name={`${name}-showHtml`}
        onChange={showHtml => setShowHtml(showHtml)}
        options={[
          {
            color: 'primary',
            label: editorLabel,
            value: false
          },
          {
            color: 'primary',
            label: htmlLabel,
            value: true
          }
        ]}
        value={showHtml}
      />

      {!showHtml && (
        <Editor
          editorClassName={classNames(
            'tw-p-3 tw-w-full disabled:tw-text-opacity-80 tw-text-sm disabled:tw-bg-gray-100 tw-border-t-0 tw-rounded-b !tw-rounded-t-none tw-z-0',
            editorClassName,
            {
              'tw-hidden': showHtml
            }
          )}
          editorState={editorState}
          name={name}
          onBlur={handleBlur}
          onEditorStateChange={nextEditorState => {
            setEditorState(nextEditorState);
            if (
              !editorState
                .getCurrentContent()
                .equals(nextEditorState.getCurrentContent())
            ) {
              const nextValue = getHTMLForEditorState(nextEditorState);
              debouncedOnChange(nextValue);
            }
          }}
          readOnly={disabled}
          toolbar={{
            options: [
              'blockType',
              'inline',
              'list',
              'link',
              'textAlign',
              'image',
              'remove',
              'history'
            ],
            image: {
              alt: {
                present: true
              },
              className: 'tw-flex tw-px-2'
            },
            blockType: {
              inDropdown: false,
              className: 'tw-flex tw-border-r tw-border-gray-500 tw-px-2'
            },
            inline: {
              inDropdown: false,
              className: 'tw-flex tw-border-r tw-border-gray-500 tw-px-2'
            },
            list: {
              inDropdown: false,
              className: 'tw-flex tw-border-r tw-border-gray-500 tw-px-2'
            },
            textAlign: {
              inDropdown: false,
              className: 'tw-flex tw-border-r tw-border-gray-500 tw-px-2',
              left: { className: 'text-align-left' },
              center: { className: 'text-align-center' },
              right: { className: 'text-align-right' },
              justify: { className: 'text-align-justify' }
            },
            link: {
              inDropdown: false,
              className: 'tw-flex tw-border-r tw-border-gray-500 tw-px-2'
            },
            remove: {
              inDropdown: false,
              className: 'tw-flex tw-px-2'
            },
            history: {
              inDropdown: false,
              className: 'tw-flex tw-border-l tw-border-gray-500 tw-px-2'
            }
          }}
          toolbarClassName={classNames(
            'tw-m-0 tw-border-solid tw-border-gray-300 tw-border-1 tw-max-h-lg tw-rounded-b-none tw-border-b-none tw-z-10 tw-relative tw-flex tw-flex-wrap',
            toolbarClassName
          )}
          wrapperClassName={classNames(
            'tw-shadow-none tw-w-full tw-z-0',
            wrapperClassName
          )}
          wrapperId={wrapperId}
          stripPastedStyles
        />
      )}

      {showHtml && (
        <RawHtml
          disabled={disabled}
          htmlSourceClassName={htmlSourceClassName}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          setEditorState={setEditorState}
          value={value}
        />
      )}
    </div>
  );
};

const RawHtml = ({
  disabled,
  htmlSourceClassName,
  name,
  onBlur,
  onChange,
  setEditorState,
  value
}) => {
  const numberOfRows = value?.split('\n').length || 1;
  const formatNumber = useFormatNumber();
  return (
    <div className="tw-w-full tw-overflow-hidden">
      <div
        className={classNames(
          'tw-relative tw-flex tw-gap-3 tw-pr-2 tw-font-mono tw-text-sm tw-leading-6',
          {
            'tw-bg-gray-100': !disabled,
            'tw-bg-gray-200': disabled
          },
          htmlSourceClassName
        )}
      >
        <ol className=" tw-bg-gray-300 tw-p-2 tw-text-right">
          {times(numberOfRows, i => (
            <li className="tw-block">{formatNumber(i + 1)}</li>
          ))}
        </ol>
        <textarea
          className={classNames(
            'HtmlEditorSource tw-w-full tw-resize-none tw-rounded tw-border-0 tw-py-2 focus:tw-outline-none',
            {
              'tw-bg-gray-100 ': !disabled,
              'tw-bg-gray-200': disabled
            }
          )}
          disabled={disabled}
          name={name}
          onBlur={onBlur}
          onChange={e => {
            onChange(e.target.value);
            const nextEditorState = getEditorStateForHTML(e.target.value);
            setEditorState(nextEditorState);
          }}
          rows={numberOfRows}
          value={value}
          wrap="off"
        />
      </div>
    </div>
  );
};

export interface HTMLEditorProps {
  className?: string;
  disabled?: boolean;
  editorClassName?: string;
  editorLabel?: string;
  htmlLabel?: string;
  htmlSourceClassName?: string;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onChange?: (value: string) => void;
  toolbarClassName?: string;
  wrapperClassName?: string;
  wrapperId?: number | string;
  value?: string;
}

const getEditorStateForHTML = value => {
  if (!value) return EditorState.createEmpty();

  const contentBlock = htmlToDraft(value);
  if (contentBlock) {
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
      contentBlock.entityMap
    );
    return EditorState.createWithContent(contentState);
  }
  return EditorState.createEmpty();
};

/**
 * This function is used to prevent the issue when the inline styles
 * for image contain "float: none;height: ;width: ;" that causes the content security policy error.
 */
const customEntityTransform = entity => {
  if (entity.type === 'IMAGE') {
    const { src, alt, alignment, height, width } = entity.data;

    let style = '';

    if (!isEmpty(height)) {
      style += `height: ${height};`;
    }

    if (!isEmpty(width)) {
      style += `width: ${width};`;
    }

    let imgHtml;

    if (!isEmpty(style)) {
      imgHtml = `<img src="${src}" alt="${alt}" style="${style}"/>`;
    } else {
      imgHtml = `<img src="${src}" alt="${alt}"/>`;
    }

    if (!isEmpty(alignment)) {
      imgHtml = `<div style="text-align:${alignment};">${imgHtml}</div>`;
    }

    return imgHtml;
  }

  // return undefined to execute the original method
  return undefined;
};

const getHTMLForEditorState = editorState => {
  const currentContent = editorState.getCurrentContent();
  const rawContent = convertToRaw(currentContent);

  const html = draftToHtml(
    rawContent,
    undefined,
    undefined,
    customEntityTransform
  );
  const rawHtml = replace(html.trim(), /&nbsp;/g, ' ');
  if (rawHtml === '<p></p>') {
    // we indicate that an empty paragraph should clear out the markup, as there is no content
    return undefined;
  }

  // there is a bug in draftToHtml. It calls getListMarkup(listBlocks, entityMap, hashtagConfig, customEntityTransform) without "directional" parameter
  // because of that the "customEntityTransform" function is used in the condition "if(directional)", it returns true and adds 'dir = "auto"' tag.
  // this was fixed in newer version https://github.com/jpuri/draftjs-to-html/pull/78
  // TODO upgrade the draftjs-to-html and remove this fix
  return replace(rawHtml, / dir = "auto"/g, '');
};

export default AuroraHTMLEditor;
